import { Avatar, AvatarFallback, AvatarImage } from 'v5/design-system/Avatar';
import { cn } from 'v5/platform/dom/cn';
import { ResourceAvatarSilhouette } from 'v5/patterns/resource/ResourceAvatarSilhouette';
import { entityInitials } from './entityInitials';

/** An entity as a picker shows it: its picture, its name and one line about it. */
export type EntityOptionData = {
  name: string;
  /** The second line (type, email); `null` shows the name alone. */
  detail: string | null;
  pictureUrl: string | null;
};

/** The entity's picture, else its initials, else a silhouette. */
export function EntityAvatar({
  entity,
  size,
  raised = false
}: {
  entity: Pick<EntityOptionData, 'name' | 'pictureUrl'>;
  size: '2xs' | 'xs' | 'sm' | 'lg';
  /** Lifted off a coloured surface, as the entity header's picture: a shadow and a white tile. */
  raised?: boolean;
}) {
  const letters = entityInitials(entity.name);
  return (
    // A menu item sizes its icons (`[&_svg,img]:size-5`): the picture fills the avatar regardless.
    // Keyed by the picture: a loaded one's status would otherwise outlive it and hide the fallback.
    <Avatar
      key={entity.pictureUrl ?? ''}
      size={size}
      className={cn(
        '[&_img]:size-full!',
        raised && 'shadow-sm shadow-black/20'
      )}
    >
      {entity.pictureUrl !== null && (
        // Mounted once loaded: it fades in over the fallback instead of popping.
        <AvatarImage
          src={entity.pictureUrl}
          alt=""
          className="animate-in fade-in-0 duration-300 motion-reduce:animate-none"
        />
      )}
      <AvatarFallback
        // With a picture coming, the initials wait: a fast one never shows them.
        delayMs={entity.pictureUrl !== null ? 300 : undefined}
        // The name beside it says who it is: the letters are decoration.
        aria-hidden
        className={cn(
          'font-semibold text-fg-muted',
          raised ? 'bg-bg-default' : 'bg-bg-subtle'
        )}
      >
        {letters === '' ? <ResourceAvatarSilhouette /> : letters}
      </AvatarFallback>
    </Avatar>
  );
}

/** An option in a picker's list: avatar, name and the detail line under it. */
export function EntityOption({ entity }: { entity: EntityOptionData }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      {/* On a highlighted row the tile would melt into it: multiply keeps it visible. */}
      <span className="contents [&_[data-slot=avatar-fallback]]:mix-blend-multiply">
        <EntityAvatar entity={entity} size="sm" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium leading-snug">{entity.name}</span>
        {entity.detail !== null && (
          <span className="truncate text-sm text-fg-muted">
            {entity.detail}
          </span>
        )}
      </span>
    </span>
  );
}

/** The picked entity in a trigger or at rest: a small avatar and the name, on one line. */
export function EntityChip({
  entity
}: {
  entity: Pick<EntityOptionData, 'name' | 'pictureUrl'>;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <EntityAvatar entity={entity} size="xs" />
      <span className="truncate">{entity.name}</span>
    </span>
  );
}
